import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { rolesApi } from '../services/api'
import { useNotificationStore } from './notifications'

export const useRoleStore = defineStore('roles', () => {
  // State
  const roles = ref([])
  const currentRole = ref(null)
  const loading = ref(false)
  const error = ref(null)
  const pagination = ref({
    total: 0,
    page: 1,
    size: 20,
    pages: 1
  })
  const filters = ref({
    name: '',
    include_deleted: false
  })

  // Getters
  const totalRoles = computed(() => pagination.value.total)
  const activeRoles = computed(() =>
    roles.value.filter(role => !role.is_deleted).length
  )
  const deletedRoles = computed(() =>
    roles.value.filter(role => role.is_deleted).length
  )

  // Actions
  const notificationStore = useNotificationStore()

  const setLoading = (isLoading) => {
    loading.value = isLoading
  }

  const setError = (errorMessage) => {
    error.value = errorMessage
    if (errorMessage) {
      notificationStore.addNotification('error', errorMessage)
    }
  }

  const clearError = () => {
    error.value = null
  }

  const fetchRoles = async (page = 1, size = 20) => {
    setLoading(true)
    clearError()

    try {
      const params = {
        page,
        size,
        ...filters.value
      }

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key]
        }
      })

      const response = await rolesApi.getRoles(params)

      roles.value = response.items
      pagination.value = {
        total: response.total,
        page: response.page,
        size: response.size,
        pages: response.pages
      }

      return response
    } catch (error) {
      console.error('Error fetching roles:', error)
      const errorMsg = error.response?.data?.detail || error.message || 'Error desconocido al cargar los roles'
      setError('No se pudieron cargar los roles. ' + errorMsg)

      // Set empty data on error to prevent UI issues
      roles.value = []
      pagination.value = {
        total: 0,
        page: 1,
        size: 20,
        pages: 1
      }

      // Don't throw, just return empty response
      return { items: [], total: 0, page: 1, size: 20, pages: 1 }
    } finally {
      setLoading(false)
    }
  }

  const fetchRole = async (id, includeDeleted = false) => {
    setLoading(true)
    clearError()

    try {
      const role = await rolesApi.getRole(id, includeDeleted)
      currentRole.value = role
      return role
    } catch (error) {
      setError('Error al cargar el rol: ' + error.message)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const createRole = async (roleData) => {
    setLoading(true)
    clearError()

    try {
      const newRole = await rolesApi.createRole(roleData)
      notificationStore.addNotification('success', `Rol "${newRole.name}" creado exitosamente`)

      // Refresh the list
      await fetchRoles(pagination.value.page, pagination.value.size)

      return newRole
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message
      setError('Error al crear el rol: ' + errorMsg)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const updateRole = async (id, roleData) => {
    setLoading(true)
    clearError()

    try {
      const updatedRole = await rolesApi.updateRole(id, roleData)
      notificationStore.addNotification('success', `Rol "${updatedRole.name}" actualizado exitosamente`)

      // Update current role if it's the one being edited
      if (currentRole.value?.id === id) {
        currentRole.value = updatedRole
      }

      // Refresh the list
      await fetchRoles(pagination.value.page, pagination.value.size)

      return updatedRole
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message
      setError('Error al actualizar el rol: ' + errorMsg)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const deleteRole = async (id) => {
    setLoading(true)
    clearError()

    try {
      await rolesApi.deleteRole(id)
      notificationStore.addNotification('success', 'Rol eliminado exitosamente')

      // Refresh the list
      await fetchRoles(pagination.value.page, pagination.value.size)
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message
      setError('Error al eliminar el rol: ' + errorMsg)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const restoreRole = async (id) => {
    setLoading(true)
    clearError()

    try {
      const restoredRole = await rolesApi.restoreRole(id)
      notificationStore.addNotification('success', `Rol "${restoredRole.name}" restaurado exitosamente`)

      // Refresh the list
      await fetchRoles(pagination.value.page, pagination.value.size)

      return restoredRole
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message
      setError('Error al restaurar el rol: ' + errorMsg)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const hardDeleteRole = async (id) => {
    setLoading(true)
    clearError()

    try {
      await rolesApi.hardDeleteRole(id)
      notificationStore.addNotification('warning', 'Rol eliminado permanentemente')

      // Refresh the list
      await fetchRoles(pagination.value.page, pagination.value.size)
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message
      setError('Error al eliminar permanentemente el rol: ' + errorMsg)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const setFilters = (newFilters) => {
    filters.value = { ...filters.value, ...newFilters }
  }

  const clearFilters = () => {
    filters.value = {
      name: '',
      include_deleted: false
    }
  }

  const clearCurrentRole = () => {
    currentRole.value = null
  }

  return {
    // State
    roles,
    currentRole,
    loading,
    error,
    pagination,
    filters,

    // Getters
    totalRoles,
    activeRoles,
    deletedRoles,

    // Actions
    fetchRoles,
    fetchRole,
    createRole,
    updateRole,
    deleteRole,
    restoreRole,
    hardDeleteRole,
    setFilters,
    clearFilters,
    clearCurrentRole,
    clearError
  }
})
